import classNames from 'classnames';
import React, { forwardRef, memo, useImperativeHandle, useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import AccessoryPicker from './rtic/AccessoryPicker';
import { CiCirclePlus, CiCircleMinus } from 'react-icons/ci';

const INIT_ADDON = {
  colorId: null,
  id: null,
  itemColor: 'Select Color',
  baseRate: null,
  rateEach: null,
};

type Props = {
  className?: string;
};

const AccessoryPickers = forwardRef(({ className }: Props, forwardedRef) => {
  const { addOns, details } = useSelector((state: any) => state.pdpItem);

  const [additionalLid, setAdditionalLid] = useState(INIT_ADDON);
  const [additionalBoot, setAdditionalBoot] = useState(INIT_ADDON);
  const [accessoryPickerExpanded, setAccessoryPickerExpanded] = useState(true);

  useImperativeHandle(
    forwardedRef,
    () => ({
      additionalLid,
      additionalBoot,
      selectedAddOns: [additionalLid, additionalBoot].filter((i) => i.colorId),
    }),
    [additionalBoot, additionalLid, addOns]
  );

  const hideAccessoryPickers = useMemo(
    () =>
      !details?.isColorWay ||
      details?.isGenericProduct ||
      details?.uom?.conversion !== 1 ||
      addOns?.length < 1,
    [addOns, details]
  );

  useEffect(() => {
    if (hideAccessoryPickers) {
      if (additionalLid.id) setAdditionalLid(INIT_ADDON);
      if (additionalBoot.id) setAdditionalLid(INIT_ADDON);
    }
  }, [hideAccessoryPickers, additionalBoot, additionalLid]);

  if (hideAccessoryPickers) return null;

  return (
    <div className={classNames('pdp-swatches__item pdp-swatches__item-additions', className)}>
      <div className='tw:rounded-button tw:border tw:border-neutrals-light-grey tw:p-4'>
        <div
          className='tw:flex tw:cursor-pointer tw:items-center tw:gap-2'
          onClick={() => setAccessoryPickerExpanded((prev) => !prev)}>
          {accessoryPickerExpanded ? <CiCircleMinus size={30} /> : <CiCirclePlus size={30} />}
          <p className='tw:mb-0 tw:select-none tw:text-base tw:font-bold tw:text-brand-black'>
            Accessorize Your {details.attributes.itemType}
          </p>
        </div>
        <div
          className={classNames('tw:flex tw:flex-col tw:gap-6 tw:transition-all', {
            'tw:max-h-0 tw:overflow-hidden': !accessoryPickerExpanded,
            'tw:mt-5 tw:max-h-[1000px] tw:overflow-y-auto tw:duration-500': accessoryPickerExpanded,
          })}>
          <AccessoryPicker
            INIT={INIT_ADDON}
            title='Extra Lid'
            setSelection={setAdditionalLid}
            selection={additionalLid}
            items={addOns.filter((item) => item.type === 'lid')}
          />
          <AccessoryPicker
            INIT={INIT_ADDON}
            title='Silicone Boot'
            setSelection={setAdditionalBoot}
            selection={additionalBoot}
            items={addOns.filter((item) => item.type === 'boot')}
          />
        </div>
      </div>
    </div>
  );
});

export default memo(AccessoryPickers);
